// Reusable CSV Import Card Component
// Can be used across different modules for importing specific data types

import React, { useState } from 'react';
import { Upload, Download, FileText, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { CSVImportService, ImportResult } from '../utils/csvImportService';
import { barberDataService } from '../services/barberDataService';
import { clientDataService } from '../services/clientDataService';
import { appointmentService } from '../services/appointmentService';
import toast from 'react-hot-toast';
import { ymd } from '../shared/dateUtils';

type ImportType = 'barber' | 'client' | 'appointment';

interface ImportProgress {
  type: ImportType;
  status: 'idle' | 'processing' | 'completed' | 'error';
  result?: ImportResult;
  file?: File;
}

interface CSVImportCardProps {
  type: ImportType;
  title: string;
  description: string;
  icon: React.ReactNode;
  onImportComplete?: (result: ImportResult) => void;
}

const CSVImportCard: React.FC<CSVImportCardProps> = ({
  type,
  title,
  description,
  icon,
  onImportComplete
}) => {
  const [importState, setImportState] = useState<ImportProgress>({
    type,
    status: 'idle'
  });

  const handleFileSelect = (file: File | null) => {
    if (!file) return;
    setImportState({ type, status: 'idle', file });
  };

  const processBarberImport = async (file: File): Promise<ImportResult> => {
    console.log('🔵 Starting barber import process...');
    const { data } = await CSVImportService.parseCSV(file);
    console.log(`🔵 Parsed ${data.length} rows from CSV`);

    const result: ImportResult = {
      success: false,
      totalRows: data.length,
      successCount: 0,
      failedCount: 0,
      errors: [],
      warnings: []
    };

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      console.log(`🔵 Processing row ${i + 2}:`, row);

      const validation = CSVImportService.validateBarberRow(row, i + 2);
      console.log(`🔵 Validation result for row ${i + 2}:`, validation);

      if (!validation.valid) {
        console.log(`❌ Row ${i + 2} validation failed:`, validation.errors);
        result.errors.push({
          row: i + 2,
          error: validation.errors.join(', '),
          data: row
        });
        result.failedCount++;
        continue;
      }

      try {
        console.log(`🔵 Adding barber to Firebase:`, validation.barber);

        // Add timeout to prevent hanging on slow Firebase calls
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Firebase timeout')), 5000)
        );

        await Promise.race([
          barberDataService.addBarber(validation.barber!),
          timeoutPromise
        ]);

        console.log(`✅ Successfully added barber`);
        result.successCount++;
      } catch (error) {
        console.error(`❌ Failed to add barber for row ${i + 2}:`, error);
        result.errors.push({
          row: i + 2,
          error: error instanceof Error ? error.message : 'Failed to add barber',
          data: row
        });
        result.failedCount++;
      }
    }

    console.log(`🔵 Import complete. Success: ${result.successCount}, Failed: ${result.failedCount}`);
    console.log(`🔵 Full result:`, result);
    result.success = result.successCount > 0;
    return result;
  };

  const processClientImport = async (file: File): Promise<ImportResult> => {
    console.log('🔵 Starting client import process...');
    const { data } = await CSVImportService.parseCSV(file);
    console.log(`🔵 Parsed ${data.length} rows from CSV`);

    const result: ImportResult = {
      success: false,
      totalRows: data.length,
      successCount: 0,
      failedCount: 0,
      errors: [],
      warnings: []
    };

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      console.log(`🔵 Processing row ${i + 2}:`, row);

      const validation = CSVImportService.validateClientRow(row, i + 2);
      console.log(`🔵 Validation result for row ${i + 2}:`, validation);

      if (!validation.valid) {
        console.log(`❌ Row ${i + 2} validation failed:`, validation.errors);
        result.errors.push({
          row: i + 2,
          error: validation.errors.join(', '),
          data: row
        });
        result.failedCount++;
        continue;
      }

      try {
        console.log(`🔵 Adding client to Firebase:`, validation.client);

        // Add timeout to prevent hanging on slow Firebase calls
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Firebase timeout')), 5000)
        );

        const addedClient = await Promise.race([
          clientDataService.addClient(validation.client!),
          timeoutPromise
        ]);

        console.log(`✅ Successfully added client:`, addedClient);
        result.successCount++;
      } catch (error) {
        console.error(`❌ Failed to add client for row ${i + 2}:`, error);
        result.errors.push({
          row: i + 2,
          error: error instanceof Error ? error.message : 'Failed to add client',
          data: row
        });
        result.failedCount++;
      }
    }

    console.log(`🔵 Import complete. Success: ${result.successCount}, Failed: ${result.failedCount}`);
    console.log(`🔵 Full result:`, result);
    result.success = result.successCount > 0;
    return result;
  };

  const processAppointmentImport = async (file: File): Promise<ImportResult> => {
    const { data } = await CSVImportService.parseCSV(file);

    const result: ImportResult = {
      success: false,
      totalRows: data.length,
      successCount: 0,
      failedCount: 0,
      errors: [],
      warnings: []
    };

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const validation = CSVImportService.validateAppointmentRow(row, i + 2);

      if (!validation.valid) {
        result.errors.push({
          row: i + 2,
          error: validation.errors.join(', '),
          data: row
        });
        result.failedCount++;
        continue;
      }

      try {
        // Find or create client
        let client = await clientDataService.searchClients(validation.appointment.clientPhone).find(c =>
          c.phone === validation.appointment.clientPhone
        );

        if (!client && validation.appointment.clientName) {
          const [firstName, ...lastNameParts] = validation.appointment.clientName.split(' ');
          const newClient = await clientDataService.addClient({
            firstName,
            lastName: lastNameParts.join(' '),
            phone: validation.appointment.clientPhone,
            email: validation.appointment.clientEmail || '',
            membershipType: 'regular',
            joinDate: new Date(),
            totalVisits: 0,
            totalSpent: 0,
            averageTicket: 0,
            rebookingRate: 0,
            noShowCount: 0,
            cancellationCount: 0,
            tags: [],
            status: 'active',
            loyaltyPoints: 0
          });
          client = newClient;
        }

        if (!client) {
          result.errors.push({
            row: i + 2,
            error: 'Could not find or create client',
            data: row
          });
          result.failedCount++;
          continue;
        }

        // Find barber
        let barberId = validation.appointment.barberId;
        if (!barberId && validation.appointment.barberName) {
          const barbers = await barberDataService.getAllBarbers(true); // incl. archived: match history to the right person
          const barber = barbers.find(b =>
            `${b.firstName} ${b.lastName}` === validation.appointment.barberName
          );
          barberId = barber?.id;
        }

        const allBarbers = await barberDataService.getAllBarbers(true); // incl. archived for dedup/fallback

        if (!barberId) {
          result.warnings.push({
            row: i + 2,
            warning: 'Barber not found, using first available',
            data: row
          });
          barberId = allBarbers[0]?.id;
        }

        // Create appointment
        const barber = allBarbers.find(b => b.id === barberId);
        const appointment = {
          clientId: client.id,
          clientName: `${client.firstName} ${client.lastName}`,
          clientPhone: client.phone,
          barberId: barberId!,
          barberName: barber ? `${barber.firstName} ${barber.lastName}` : 'Unassigned',
          service: validation.appointment.service,
          date: validation.appointment.date,
          time: validation.appointment.time,
          duration: validation.appointment.duration,
          price: validation.appointment.price,
          status: validation.appointment.status,
          notes: validation.appointment.notes,
          source: 'import' as const,
          reminderSent: false
        };

        // Non-destructive import: upsert by business key so re-dropping a file UPDATES (fills tips/price/
        // status) instead of duplicating. BEHAVIOR-PIN: project-slik-production-safety-net-2026-09-11.
        await appointmentService.upsertImportedAppointment(appointment);

        // Handle recurring appointments
        if (validation.appointment.recurring) {
          const pattern = validation.appointment.recurringPattern;
          const endDate = new Date(validation.appointment.recurringEndDate);
          const startDate = new Date(validation.appointment.date);

          let currentDate = new Date(startDate);
          currentDate.setDate(currentDate.getDate() + (pattern === 'weekly' ? 7 : pattern === 'biweekly' ? 14 : 30));

          while (currentDate <= endDate) {
            const recurringAppointment = {
              ...appointment,
              date: ymd(currentDate)
            };

            await appointmentService.upsertImportedAppointment(recurringAppointment);
            currentDate.setDate(currentDate.getDate() + (pattern === 'weekly' ? 7 : pattern === 'biweekly' ? 14 : 30));
          }
        }

        result.successCount++;
      } catch (error) {
        result.errors.push({
          row: i + 2,
          error: error instanceof Error ? error.message : 'Failed to create appointment',
          data: row
        });
        result.failedCount++;
      }
    }

    result.success = result.successCount > 0;
    return result;
  };

  const handleImport = async () => {
    if (!importState.file) {
      toast.error('Please select a file first');
      return;
    }

    setImportState({ ...importState, status: 'processing' });
    toast.loading(`Importing ${type}s...`, { id: `import-${type}` });

    try {
      let result: ImportResult;

      if (type === 'barber') {
        result = await processBarberImport(importState.file);
      } else if (type === 'client') {
        result = await processClientImport(importState.file);
      } else {
        result = await processAppointmentImport(importState.file);
      }

      setImportState({ ...importState, status: 'completed', result });

      if (result.success) {
        toast.success(
          `Successfully imported ${result.successCount} ${type}(s)${result.failedCount > 0 ? ` (${result.failedCount} failed)` : ''}`,
          { id: `import-${type}`, duration: 5000 }
        );
        onImportComplete?.(result);
      } else {
        toast.error(`Import failed: ${result.errors.length} errors`, { id: `import-${type}` });
      }
    } catch (error) {
      setImportState({ ...importState, status: 'error' });
      toast.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`, { id: `import-${type}` });
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border-2 border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {icon}
          <div>
            <h3 className="text-xl font-bold">{title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
          </div>
        </div>
        <button
          onClick={() => CSVImportService.downloadTemplate(type)}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors"
        >
          <Download className="h-4 w-4" />
          Download Template
        </button>
      </div>

      {/* File Upload */}
      <div className="mb-4">
        <label className="block w-full">
          <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            importState.file
              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
              : 'border-gray-300 hover:border-gray-400 dark:border-gray-600'
          }`}>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
              className="hidden"
              disabled={importState.status === 'processing'}
            />
            {importState.file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium">{importState.file.name}</span>
              </div>
            ) : (
              <div>
                <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Drop CSV file here or click to browse
                </p>
              </div>
            )}
          </div>
        </label>
      </div>

      {/* Import Button */}
      <button
        onClick={handleImport}
        disabled={!importState.file || importState.status === 'processing'}
        className={`w-full py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
          !importState.file || importState.status === 'processing'
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-purple-600 text-white hover:bg-purple-700'
        }`}
      >
        {importState.status === 'processing' ? (
          <>
            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
            Processing...
          </>
        ) : (
          <>
            <ArrowRight className="h-5 w-5" />
            Import {title}
          </>
        )}
      </button>

      {/* Results */}
      {importState.result && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">Import Results:</span>
            {importState.result.success ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Total Rows:</span>
              <span className="font-medium">{importState.result.totalRows}</span>
            </div>
            <div className="flex justify-between text-green-600">
              <span>Successful:</span>
              <span className="font-medium">{importState.result.successCount}</span>
            </div>
            {importState.result.failedCount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Failed:</span>
                <span className="font-medium">{importState.result.failedCount}</span>
              </div>
            )}
            {importState.result.warnings.length > 0 && (
              <div className="flex justify-between text-yellow-600">
                <span>Warnings:</span>
                <span className="font-medium">{importState.result.warnings.length}</span>
              </div>
            )}
          </div>

          {/* Error Details */}
          {importState.result.errors.length > 0 && (
            <details className="mt-3">
              <summary className="text-sm text-red-600 cursor-pointer font-medium">
                View Errors ({importState.result.errors.length})
              </summary>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {importState.result.errors.slice(0, 10).map((error, idx) => (
                  <div key={idx} className="text-xs p-2 bg-red-50 dark:bg-red-900/20 rounded">
                    <span className="font-medium">Row {error.row}:</span> {error.error}
                  </div>
                ))}
                {importState.result.errors.length > 10 && (
                  <div className="text-xs text-gray-500">
                    ...and {importState.result.errors.length - 10} more errors
                  </div>
                )}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default CSVImportCard;
